import { Button, Card, Descriptions, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { adminLinks } from '../../lib/adminLinks';
import { PersonAvatar } from '../../lib/PersonAvatar';
import { QueryError } from '../../lib/QueryState';
import { formatCoins } from '../../lib/money';
import { plain } from '../../lib/labels';
import { usePermissions } from '../../auth/store';
import { userLabel, userSecondary, validDisplayName } from '../../lib/listenerName';

/** The slice of `GET /admin/users/:id` a payment investigator needs. */
interface UserSummary {
  id: string;
  handle: string;
  /** The user's own Display Name — absent on older APIs. */
  displayName?: string | null;
  avatarSeed?: string;
  phoneLast4?: string;
  createdAt: string;
  deletedAt: string | null;
  blockedAt: string | null;
  balanceCoins?: number;
  totalCalls: number;
  listener: { id: string; status: string; displayName: string } | null;
}

/**
 * Who paid: a compact user card beside a recharge, so "what happened to this
 * user's recharge" is answered without leaving the drawer. Loads only with
 * `users.detail.view` (the API audits that read, as it should).
 */
export function RechargeUserCard({
  userId,
  onShowAllAttempts,
}: {
  userId: string;
  /** Filter the list behind the drawer to this user's attempts. */
  onShowAllAttempts?: () => void;
}) {
  const { can } = usePermissions();
  const allowed = can('users.detail.view');
  const q = useQuery({
    queryKey: ['admin', 'user-detail', userId],
    queryFn: async () => (await api.get<UserSummary>(`/admin/users/${userId}`)).data,
    enabled: allowed,
    staleTime: 60_000,
  });

  if (!allowed) return null;
  const u = q.data;
  return (
    <Card size="small" title="Who paid" loading={q.isLoading} data-testid="recharge-user-card">
      {q.isError && <QueryError error={q.error} what="the user summary" onRetry={() => void q.refetch()} />}
      {u && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space align="center">
            <PersonAvatar name={validDisplayName(u.displayName) ?? u.handle} seed={u.avatarSeed ?? u.id} size={36} />
            <div>
              <Link to={adminLinks.user(u.id)} style={{ fontWeight: 600 }}>
                {userLabel(u)}
              </Link>
              {userSecondary(u) && (
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {userSecondary(u)}
                  </Typography.Text>
                </div>
              )}
              <div>
                <Typography.Text type="secondary" copyable={{ text: u.id }} style={{ fontSize: 11 }}>
                  {u.id.slice(0, 8)}
                </Typography.Text>
                {u.phoneLast4 && (
                  <Typography.Text type="secondary" style={{ fontSize: 11, marginInlineStart: 8 }}>
                    ••• {u.phoneLast4}
                  </Typography.Text>
                )}
              </div>
            </div>
            {u.deletedAt ? (
              <Tag color="error">Deleted</Tag>
            ) : u.blockedAt ? (
              <Tag color="error">Blocked</Tag>
            ) : (
              <Tag color="success">Active</Tag>
            )}
          </Space>
          <Descriptions size="small" column={2} colon={false}>
            <Descriptions.Item label="Coin balance now">
              <strong>{typeof u.balanceCoins === 'number' ? formatCoins(u.balanceCoins) : '—'}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Total calls">{u.totalCalls}</Descriptions.Item>
            <Descriptions.Item label="Joined">{new Date(u.createdAt).toLocaleDateString()}</Descriptions.Item>
            <Descriptions.Item label="Listener">
              {u.listener ? <Tag color="magenta">{plain(u.listener.status)}</Tag> : 'No'}
            </Descriptions.Item>
          </Descriptions>
          <Space wrap>
            {onShowAllAttempts && (
              <Button size="small" onClick={onShowAllAttempts}>
                All attempts by this user
              </Button>
            )}
            <Link to={adminLinks.user(u.id)}>
              <Button size="small">Open user</Button>
            </Link>
            <Link to={adminLinks.userCalls(u.id)}>
              <Button size="small">Calls</Button>
            </Link>
          </Space>
        </Space>
      )}
    </Card>
  );
}
